import { graphql } from "gatsby";
import React from "react";
import SectionBreedList, {
	getSectionBreedListProps,
} from "../../../containers/sections/SectionBreedList/SectionBreedList";
import SectionHelpAdviceNewsletterPromo from "../../../containers/sections/SectionHelpAdviceNewsletterPromo";
import SectionOurStoriesFilteredList from "../../../containers/sections/SectionOurStoriesFilteredList/SectionOurStoriesFilteredList";
import {
	mapPromotedItemToBreedCard,
	mapPromotedItemToSignpostCard,
} from "../../../utils/mappers";
import SectionHighlightedTextBlock from "../../sections/SectionHighlightedTextBlock";
import SectionSignPostCardCarousel from "../../sections/SectionSignPostCardCarousel/SectionSignPostCardCarousel";
import SectionSpotlightDogs from "../../sections/SectionSpotlightDogs";
import SectionSuccessStoryCarousel from "../../sections/SectionSuccessStoryCarousel/SectionSuccessStoryCarousel";
import SectionTextOnlySignpostCard, {
	getSectionTextOnlySignpostCardProps,
} from "../../sections/SectionTextOnlySignpostCard/SectionTextOnlySignpostCard";
import SectionWithList from "../../sections/SectionWithList";
import SectionArticleOrEventCards from "../SectionArticleOrEventCards";
import { getSectionArticleOrEventCardsProps } from "../SectionArticleOrEventCards/SectionArticleOrEventCards";
import SectionBehaviourHelplineForm, {
	getSectionBehaviourHelplineFormProps,
} from "../SectionBehaviourHelplineForm/SectionBehaviourHelplineForm";
import SectionBodyCondition from "../SectionBodyCondition/SectionBodyCondition";
import SectionBreedCarousel, {
	getSectionBreedCarouselProps,
} from "../SectionBreedCarousel/SectionBreedCarousel";
import SectionBreedSearch, {
	getSectionBreedSearchProps,
} from "../SectionBreedSearch/SectionBreedSearch";
import SectionBreedSearchFilteredList from "../SectionBreedSearchFilteredList/SectionBreedSearchFilteredList";
import SectionCmsMap from "../SectionCmsMap/SectionCmsMap";
import SectionContactDetails, {
	getSectionContactDetailsProps,
} from "../SectionContactDetails/SectionContactDetails";
import SectionContactForm, {
	getSectionContactFormProps,
} from "../SectionContactForm/SectionContactForm";
import SectionDogSchoolSearch from "../SectionDogSchoolSearch";
import SectionDogSearch from "../SectionDogSearch";
import SectionDogSearchSuggestions from "../SectionDogSearchSuggestions";
import SectionDynamicDogByAge from "../SectionDynamicDogByAge/SectionDynamicDogByAge";
import SectionDynamicDogBySize from "../SectionDynamicDogBySize/SectionDynamicDogBySize";
import { getSectionHighlightedTextBlockProps } from "../SectionHighlightedTextBlock/SectionHighlightedTextBlock";
import SectionLargeFeaturedPromo, {
	getSectionLargeFeaturedPromoProps,
} from "../SectionLargeFeaturedPromo/SectionLargeFeaturedPromo";
import SectionOurStoriesCarouselByTopic from "../SectionOurStoriesCarouselByTopic/SectionOurStoriesCarouselByTopic";
import SectionOurStoriesListByTopic from "../SectionOurStoriesListByTopic/SectionOurStoriesListByTopic";
import {
	SectionQuickLinks,
	SectionQuickLinksEmbedded,
	getSectionQuickLinksProps,
} from "../SectionQuickLinks/SectionQuickLinks";
import SectionSearchSite from "../SectionSearchSite";
import { getSectionSearchSiteProps } from "../SectionSearchSite/SectionSearchSite";
import { getSectionSpotlightDogsProps } from "../SectionSpotlightDogs/SectionSpotlightDogs";
import SectionStandardSignPostCards from "../SectionStandardSignPostCards";
import { getSectionStandardSignPostCardsProps } from "../SectionStandardSignPostCards/SectionStandardSignPostCards";
import SectionSuccessStoryCards from "../SectionSuccessStoryCards/SectionSuccessStoryCards";
import SectionThreeColCardsDoubleFeature from "../SectionThreeColCardsDoubleFeature/SectionThreeColCardsDoubleFeature";
import SectionThreeColCardsOneFeature from "../SectionThreeColCardsOneFeature/SectionThreeColCardsOneFeature";
import SectionUnderdogPromotedDogs from "../SectionUnderdogPromotedDogs/SectionUnderdogPromotedDogs";
import SectionVolunteeringRoleLookup, {
	getSectionVolunteeringRoleLookupProps,
} from "../SectionVolunteeringRoleLookup/SectionVolunteeringRoleLookup";
import { getSectionWithListProps } from "../SectionWithList/SectionWithList";

interface Props {
	section: Queries.SectionDataFragment;
	withPageBreak?: boolean;
	displayOption?: "all" | "accordion" | "loader";
	isLastSection?: boolean;
	forceFlatCards?: boolean;
	isHomePage?: boolean;
	isChild?: boolean;
}

export const SectionToDisplay: React.FC<Props> = ({
	section,
	isLastSection,
	forceFlatCards = false,
	isHomePage = false,
	isChild = false,
	...rest
}) => {
	if (!section) {
		console.error("SectionToDisplay: Empty field_section received.");
		return null;
	}
	switch (section.relationships.field_component_type.name) {
		case "ArticleOrEventCards":
			return (
				<SectionArticleOrEventCards
					{...getSectionArticleOrEventCardsProps(section)}
					forceFlatCards={forceFlatCards}
					{...rest}
				/>
			);
		case "BodyCondition":
			return <SectionBodyCondition isChild={isChild} />;
		case "BreedCarousel":
			return (
				<SectionBreedCarousel
					key={section.title}
					items={section.promotedLinks.map(mapPromotedItemToBreedCard)}
					{...getSectionBreedCarouselProps(section)}
				/>
			);
		case "BreedList":
			return (
				<SectionBreedList
					key={section.title}
					{...getSectionBreedListProps(section)}
				/>
			);
		case "BreedSearch":
			return (
				<SectionBreedSearch
					key={section.title}
					{...getSectionBreedSearchProps(section)}
				/>
			);
		case "BreedSearchListAndFilters":
			return <SectionBreedSearchFilteredList key={section.title} />;
		case "ThreeColCardsDoubleFeature":
			return (
				<SectionThreeColCardsDoubleFeature
					key={section.title}
					title={section.field_front_end_title}
					items={section.promotedLinks.map(mapPromotedItemToSignpostCard)}
					isHomePage={isHomePage}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "ThreeColCardsOneFeature":
			return (
				<SectionThreeColCardsOneFeature
					key={section.title}
					items={section.promotedLinks.map(mapPromotedItemToSignpostCard)}
					isHomePage={isHomePage}
				/>
			);

		case "StoriesCarouselByTopic":
			return (
				<SectionOurStoriesCarouselByTopic
					key={section.title}
					heading={section.field_front_end_title}
					topic={section.relationships.field_related_topic.name}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "StoriesListByTopic":
			return (
				<SectionOurStoriesListByTopic
					key={section.title}
					heading={section.field_front_end_title}
					topic={section.relationships.field_related_topic.name}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "QuickLinks":
			return (
				<SectionQuickLinks
					{...getSectionQuickLinksProps(
						section,
						"primary",
						undefined,
						isHomePage,
					)}
				/>
			);
		case "QuickLinksInset":
			return (
				<SectionQuickLinksEmbedded
					{...getSectionQuickLinksProps(
						section,
						isHomePage ? "primary" : "alternative",
					)}
				/>
			);

		case "QuickLinksInsetWithSearch":
			return (
				<SectionQuickLinksEmbedded
					{...getSectionQuickLinksProps(
						section,
						isHomePage ? "primary" : "alternative",
					)}
					embedded={true}
					withSearch={{
						name: "search-input",
						title: section.field_front_end_title,
						baseSearchUrl: "/search/",
						placeholder: "Enter your search query",
						justInput: true,
					}}
					theme={isHomePage ? "primary" : "alternative"}
				/>
			);
		case "SpotlightDogs":
			return (
				<SectionSpotlightDogs
					key={section.title}
					{...getSectionSpotlightDogsProps(section)}
				/>
			);
		case "SectionWithList":
			return (
				<SectionWithList
					key={section.title}
					{...getSectionWithListProps(section)}
				/>
			);
		case "StandardSignPostCards":
			return (
				<SectionStandardSignPostCards
					key={section.title}
					{...getSectionStandardSignPostCardsProps(section)}
					forceFlatCards={forceFlatCards}
					{...rest}
				/>
			);
		case "SuccessStoryCards":
			return (
				<SectionSuccessStoryCards
					key={section.title}
					isSuccessStories={true}
					{...getSectionStandardSignPostCardsProps(section)}
					forceFlatCards={forceFlatCards}
					{...rest}
				/>
			);

		case "TextOnlySignpostCard":
			return (
				<SectionTextOnlySignpostCard
					key={section.title}
					{...getSectionTextOnlySignpostCardProps(section)}
				/>
			);
		case "SuccessStoryCarousel":
			return <SectionSuccessStoryCarousel key={section.title} data={section} />;
		case "SignPostCardCarousel":
			return (
				<SectionSignPostCardCarousel
					key={section.title}
					title={section.field_front_end_title}
					htmlIntro={section.field_section_short_description?.value}
					items={section.promotedLinks.map(mapPromotedItemToSignpostCard)}
					link={
						section.field_link?.uri && {
							url: section.field_link.uri,
							title: section.field_link.title,
						}
					}
					isLastSection={isLastSection}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "HighlightedTextBlock":
			return (
				<SectionHighlightedTextBlock
					key={section.title}
					{...getSectionHighlightedTextBlockProps(section)}
				/>
			);
		case "NewsletterPromo":
			return (
				<SectionHelpAdviceNewsletterPromo
					key={section.title}
					title={section.field_front_end_title}
					body={section.field_section_short_description?.value}
					buttonText={section.field_link.title}
					buttonUrl={section.field_link.uri}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "DogsTrustContactForm":
			return (
				<SectionContactForm
					key={section.title}
					{...getSectionContactFormProps(section)}
				/>
			);
		case "DogsTrustContactDetails":
			return (
				<SectionContactDetails
					key={section.title}
					{...getSectionContactDetailsProps(section)}
				/>
			);
		case "DogsTrustBehaviourHelplineForm":
			return (
				<SectionBehaviourHelplineForm
					key={section.title}
					{...getSectionBehaviourHelplineFormProps(section)}
				/>
			);

		case "Volunteering-Role-Lookup":
			return (
				<SectionVolunteeringRoleLookup
					key={section.title}
					{...getSectionVolunteeringRoleLookupProps(section)}
				/>
			);
		case "OurStoriesFilteredList":
			return <SectionOurStoriesFilteredList key={section.title} />;
		case "SiteSearchBox":
			return (
				<SectionSearchSite
					{...getSectionSearchSiteProps(section)}
					isHomepage={isHomePage}
				/>
			);
		case "DogSearch":
			return <SectionDogSearch />;
		case "DogSchoolSearch":
			return <SectionDogSchoolSearch />;
		case "SectionPromotedUnderdogs":
			return (
				<SectionUnderdogPromotedDogs
					key={section.title}
					title={section.field_front_end_title}
					htmlIntro={section.field_section_short_description?.value}
					currentDogId=""
					maxItems={
						section.field_max_items_to_display
							? section.field_max_items_to_display
							: 18
					}
					jumpLinksTitle={section.field_jump_links_title}
				/>
			);
		case "SectionDynamicDogBySize":
			return (
				<SectionDynamicDogBySize
					key={section.title}
					currentDogId=""
					maxItems={
						section.field_max_items_to_display
							? section.field_max_items_to_display
							: 18
					}
					title={section.field_front_end_title}
					htmlIntro={section.field_section_short_description?.value}
					jumpLinksTitle={section.field_jump_links_title}
					filteredSizeGroups={section.relationships.field_filter_by_dog_size.map(
						(size) => size.name,
					)}
				/>
			);
		case "SectionDynamicDogByAge":
			return (
				<SectionDynamicDogByAge
					key={section.title}
					currentDogId=""
					maxItems={
						section.field_max_items_to_display
							? section.field_max_items_to_display
							: 18
					}
					title={section.field_front_end_title}
					htmlIntro={section.field_section_short_description?.value}
					jumpLinksTitle={section.field_jump_links_title}
					filteredAgeGroup={section.field_filter_by_dog_age}
				/>
			);
		case "DogSearchSuggestions":
			return <SectionDogSearchSuggestions />;
		case "LargeFeaturedPromo":
			return (
				<SectionLargeFeaturedPromo
					key={section.title}
					{...getSectionLargeFeaturedPromoProps(section)}
				/>
			);
		case "Map":
			return (
				<SectionCmsMap
					key={section.title}
					mapType={section.relationships.field_related_map_type_content?.name}
					cmsMapCards={section.cmsMapCards as Queries.CmsMapCardFragment[]}
				/>
			);
		default:
			return null;
	}
};

export default SectionToDisplay;

export const fragment = graphql`
    fragment ParagraphImagesData on paragraph__image {
        __typename
        id
        relationships {
            field_image {
                name
                field_media_image {
                    alt
                    title
                    width
                    height
                    uri
                }
            }
        }
    }
 fragment PromotionData on node__promotion {
        id
        field_title_front_end
        field_text
        field_promo_link {
            uri
            title
        }
        field_show_on_dog_listings
        relationships {
            field_promo_image {
                ...MediaImageData
            }
            field_promotion_type {
                name
            }
        }
    }

    fragment SectionData on node__section {
        __typename
        title
        field_front_end_title
        field_jump_links_title
        field_filter_by_dog_age
        field_min_items_to_display
        field_max_items_to_display
        field_json_config
        field_section_footer_text {
            processed
            value
        }
        field_link {
            title
            uri
        }
        field_section_short_description {
            processed
            value
        }
        relationships {
            field_method_of_ordering_items {
                name
            }
            field_component_type {
                name
            }
            field_extra_section_info {
                field_extra_information {
                    value
                }
            }
            field_large_featured_promo_image {
                name
            }
            field_related_topic {
                name
            }
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_related_map_type_content {
                name
            }
            field_filter_by_dog_size {
                name
            }
        }
        promotedLinks {
            ...PromoteToUnionData
        }
        cmsMapCards {
            ...CmsMapCard
        }
    }
    fragment SignpostUnionData on SignpostUnion {
        __typename
        ...ArticleSignPostData
        ...AppealSignPostData
        ...ExternalSignPostData
        ...StaticInternalPageReferenceData
        ...LandingPageSignPostData
        ...OurStorySignPostData
    }

    fragment ArticleSignPostData on node__article {
        __typename
        title
        field_short_description
        field_short_link_text
        path {
            alias
        }
        field_node_weight
        relationships {
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
            field_related_topics_categories {
                name
            }
        }
    }

    fragment AppealSignPostData on node__appeal {
        __typename
        title
        path {
            alias
        }
        field_short_description
        field_short_link_text
        relationships {
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }

    fragment OurStorySignPostData on node__our_story {
        __typename
        title
        field_short_description
        field_short_link_text
        path {
            alias
        }
        field_node_weight
        relationships {
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }

    fragment ExternalSignPostData on node__external_signposts {
        __typename
        title
        field_external_signpost_summary
        field_link {
            title
            uri
        }
        relationships {
            field_image {
                field_media_image {
                    alt
                    uri
                }
            }
        }
    }

    fragment StaticInternalPageReferenceData on node__static_internal_page_reference {
        __typename
        title
        field_summary
        field_link {
            title
            uri
        }
        field_node_weight
        relationships {
            field_image {
                field_media_image {
                    alt
                    uri
                }
            }
            field_icon {
                field_media_image {
                    alt
                    uri
                }
            }
        }
    }

    fragment LandingPageSignPostData on node__landing_page {
        __typename
        title
        field_intro_text {
            processed
            value
        }
        field_short_link_text
        field_priority_weight
        path {
            alias
        }
        relationships {
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_images {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }

    fragment PromotedArticle on node__article {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_body {
            value
        }
        created
        field_author
        field_is_vet_friendly_clinic
        field_short_description
        field_short_link_text
        field_show_newsletter_promo
        field_meta_tags {
            ...MetaTags
        }
        relatedTopicLinks {
            title
            uri
        }
        relationships {
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_related_help_advice_articl {
                ...ContentUnionData
            }
            field_category_type {
                name
            }
        }
    }

    fragment PromotedAppeal on node__appeal {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_short_description
        field_short_link_text
        field_meta_tags {
            ...MetaTags
        }
        relationships {
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
        }
    }

    fragment PromotedLandingPage on node__landing_page {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            title
            url
        }

        field_pre_heading
        field_intro_text {
            processed
            value
        }
        field_is_sub_landing_page
        field_short_link_text
        field_meta_tags {
            title
            description
            abstract
            keywords
            canonical_url
            robots
            og_type
            og_url
            og_title
            og_description
            og_image_url
            twitter_cards_type
            twitter_cards_description
            twitter_cards_site
            twitter_cards_title
            twitter_cards_page_url
            twitter_cards_image
        }
        relationships {
            field_intro_type {
                name
            }
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_images {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }
    fragment PromotedOurStory on node__our_story {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_body {
            value
        }
        field_author
        field_short_description
        field_short_link_text
        field_date
        field_show_newsletter_promo
        field_meta_tags {
            ...MetaTags
        }
        relatedStoryTopicTags {
            title
        }
        relationships {
            field_icon {
                field_media_image {
                    uri
                    alt
                }
            }
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
            field_category_type {
                name
            }
        }
    }
    fragment PromotedBreed on node__breed {
        __typename
        title
        path {
            alias
        }
        breadcrumbs {
            ...BreadCrumb
        }
        field_intro_copy
        relationships {
            field_featured_image {
                field_media_image {
                    uri
                    alt
                }
            }
        }
    }

    fragment CmsMapCard on node__map_card {
        __typename
        status
        id
        title
        field_date
        field_google_maps_link
        field_latitude
        field_link {
            title
            uri
        }
        field_map_card_location
        field_longitude
        field_secondary_info_text
        field_secondary_info_title
        field_subtitle
        field_tertiary_info_text {
            processed
            value
        }
        relationships {
            field_content_type {
                name
                pinIconUrl
                pinIconSelectedUrl
                relationships {
                    field_map_pin_icon {
                        relationships {
                            field_media_file {
                                uri {
                                    value
                                }
                            }
                        }
                    }
                    field_map_pin_icon_selected {
                        relationships {
                            field_media_file {
                                uri {
                                    value
                                }
                            }
                        }
                    }
                }
            }
        }
    }
    
    fragment PromoteToUnionData on PromoteToUnion {
        __typename
        ...PromotedArticle
        ...PromotedAppeal
        ...ExternalSignPostData
        ...StaticInternalPageReferenceData
        ...PromotedLandingPage
        ...PromotedOurStory
        ...PromotedBreed
        ...Dog
    }
`;
